import { Trans, useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { Button } from "@offstreetparking/design-system/forms";
import { Heading, Link, Text } from "@offstreetparking/design-system/typography";
import { useCallback, useEffect, useRef, useState } from "react";
import { offstreetClient } from "../../../../api/client";
import type { RouterOutputs } from "../../../../api/types";
import { usePortalFlowHost } from "../../../../host";
import { PRIVACY_URL, TERMS_URL } from "../../../../lib/legal-urls";
import { ProrationInfoBanner } from "../../../dataDisplay/parkingDetails";
import { PaymentErrorAlert } from "../../../forms/Alert";
import { usePermitFormActions, usePermitFormData } from "../../PermitContextProvider";
import { usePermitWizardSettings } from "../../PermitContextProvider/PermitContextProvider";
import type { PermitFormData, PermitPostResultData } from "../../PermitContextProvider/types";
import { useSavedPermits } from "../../hooks/useSavedPermits";
import type { PermitTypeDto } from "../../types";
import { InvoiceDetails } from "./components/InvoiceDetails";
import { type GatewayPaymentError, PaymentFormError } from "./types";
export type PaymentFormProps = {
	permitType: PermitTypeDto;
	locationLookupId?: string;
	tenantId?: number;
	time: {
		startDateTime: string;
		endDateTime: string;
		timezone: string;
	};
	invoice?: {
		total: number;
		subtotal: number;
		taxAndFees: {
			description: string;
			longDescription?: string;
			percent?: number;
			fixedAmount?: number;
			total: number;
		}[];
		discountItems?: {
			description: string;
			total: number;
			type?: "Proration";
		}[];
		fixedRateItems?: {
			style: string;
			amount: number;
			units: number;
			total: number;
		}[];
	};
	email?: string;
	vehicles?: {
		plate: string;
		state: string;
	}[];
	currency: string;
	locale: string;
	formId: string;
	onSubmit: (data: PermitPostResultData) => void;
	setDuplicatedPermit: ({
		plate,
		permitIntervals,
		publicIdentifier,
	}: {
		plate: string;
		permitIntervals: PermitFormData["permitIntervals"];
		publicIdentifier: string;
	}) => void;
};
export const PaymentForm = ({
	permitType,
	locationLookupId,
	tenantId,
	invoice,
	time,
	email,
	vehicles,
	locale,
	formId,
	onSubmit,
	setDuplicatedPermit,
}: PaymentFormProps) => {
	const { addPermit } = useSavedPermits();
	if (!time.startDateTime || !time.endDateTime) {
		return;
	}
	const { t } = useLingui();
	const { getPermitPostData, setError, setIsProcessingPermit } = usePermitFormActions();
	const wizardSettings = usePermitWizardSettings();
	const permitFormData = usePermitFormData();
	const selectedChoice =
		wizardSettings.choices.find((choice) => choice.permitTypeId === permitFormData.parkingChoiceId) ??
		wizardSettings.choices[0];
	const [{ paymentError, displayError }, setPaymentError] = useState<{
		paymentError?: GatewayPaymentError;
		displayError: boolean;
	}>({
		displayError: false,
	});
	const [paymentSession, setPaymentSession] =
		useState<RouterOutputs["paymentSession"]["createPaymentSessionPublic"]>();
	const [retryError, setRetryError] = useState<PaymentFormError | null>(null);
	const { mutate: createPaymentSessionPublic } =
		offstreetClient.paymentSession.createPaymentSessionPublic.useMutation();
	const { PaymentGatewayPanel: GatewayPanel } = usePortalFlowHost();
	const handlePaymentSuccess = useCallback(
		(publicIdentifier: string) => {
			if (locationLookupId) {
				addPermit(locationLookupId, {
					publicIdentifier,
					publicAccessToken: paymentSession?.permit?.publicAccessToken ?? "",
				});
			}
			onSubmit({
				publicIdentifier,
				publicAccessToken: paymentSession?.permit?.publicAccessToken ?? undefined,
			});
		},
		[addPermit, locationLookupId, onSubmit, paymentSession],
	);

	const handleRetry = useCallback(() => {
		setRetryError(null);
		setPaymentError({ displayError: false });
		setPaymentSession(undefined);
	}, []);

	const [sessionAttempt, setSessionAttempt] = useState(0);
	const sessionRetryTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger payment session once its mounted, or if a timeout error occurs
	useEffect(() => {
		if (!paymentSession) {
			setIsProcessingPermit(true);
			const permit = getPermitPostData();
			createPaymentSessionPublic(
				{
					...permit,
					tenantId,
					// Sent for every platform because the platform isn't known until the session comes
					// back; only redirect-flow providers (TouchNet) consume it.
					returnUrl: `${window.location.origin}/${locale}/payments/touchnet/callback?resume=${encodeURIComponent(window.location.pathname + window.location.search)}`,
				},
				{
					onSuccess: (data) => {
						if (data.session) {
							setPaymentSession(data);
						} else if (data.permit.isPaymentPending && sessionAttempt < 3) {
							// No session but the permit is unpaid: another request for this permit is mid-flight
							// (double-mount, second tab). Not a duplicate — wait out the server's race window and
							// re-request; the stale session gets superseded and a fresh one comes back.
							sessionRetryTimeout.current = setTimeout(
								() => setSessionAttempt(sessionAttempt + 1),
								3_000,
							);
						} else if (data.permit.isPaymentPending) {
							setRetryError(PaymentFormError.UNCONFIRMED);
							setIsProcessingPermit(false);
						} else {
							setDuplicatedPermit({
								plate: data.permit.permit_vehicles_vehicle[0]?.vehicle.plate!,
								permitIntervals: data.permit.permitIntervals.map((interval) => ({
									startDateTimeISO: interval.startDateTime,
									endDateTimeISO: interval.endDateTime,
								})),
								publicIdentifier: data.permit.publicIdentifier,
							});
						}
					},
					onError: (error) => {
						setError(error);
					},
					onSettled: () => {
						setIsProcessingPermit(false);
					},
				},
			);
		}
		return () => clearTimeout(sessionRetryTimeout.current);
	}, [paymentSession, tenantId, sessionAttempt]);

	return (
		<div className="space-y-4">
			<Heading>{t`Enter Payment Information`}</Heading>
			{invoice?.discountItems?.some((item) => item.type === "Proration") && (
				<ProrationInfoBanner
					fullTermIntervals={selectedChoice?.context?.fixedIntervals}
					timezone={selectedChoice?.time.timezone}
				/>
			)}
			<InvoiceDetails
				permitType={permitType}
				invoice={invoice}
				vehicles={vehicles}
				email={email}
				time={time}
				paymentSchedule={selectedChoice?.paymentSchedule}
			/>

			{retryError ? (
				<div className="space-y-3">
					{retryError === PaymentFormError.DECLINED ? (
						<Alert
							type="error"
							title={t`Card declined`}
							message={t`Your payment didn't complete. Re-enter your card details and try again.`}
						/>
					) : (
						<Alert
							type="error"
							title={t`Payment not confirmed`}
							message={t`We couldn't confirm whether your payment went through. Do not try again — check your permits and email, and contact support if you don't see a confirmation.`}
						/>
					)}
					{retryError === PaymentFormError.DECLINED && <Button onClick={handleRetry}>{t`Try again`}</Button>}
				</div>
			) : (
				displayError && <PaymentErrorAlert error={paymentError} />
			)}

			{!retryError && GatewayPanel && (
				<GatewayPanel
					paymentSession={paymentSession}
					formId={formId}
					locationLookupId={locationLookupId}
					onSuccess={handlePaymentSuccess}
					onRetryableError={(error) => {
						setRetryError(error);
						setIsProcessingPermit(false);
					}}
					onPaymentError={(error) => {
						setPaymentError({
							paymentError: error,
							displayError: true,
						});
						setIsProcessingPermit(false);
						if (!error) {
							setPaymentSession(undefined);
						}
					}}
					setIsProcessing={(isProcessing) => setIsProcessingPermit(isProcessing)}
				/>
			)}
			{!retryError && !GatewayPanel && <PaymentErrorAlert />}
			<Text className="my-4">
				<Trans>
					By continuing, I accept Offstreet’s{" "}
					<Link href={TERMS_URL} isExternal>
						Terms of Service
					</Link>{" "}
					and{" "}
					<Link href={PRIVACY_URL} isExternal>
						Privacy Policy
					</Link>
					.
				</Trans>
			</Text>
		</div>
	);
};
